import {
  EXPORT_JOB_BUDGET_MS,
  EXPORT_JOB_ERROR_STREAK,
  EXPORT_JOB_LOST_AFTER_MS,
  EXPORT_JOB_POLL_INTERVAL_MS,
  EXPORT_JOB_SLOW_POLL_INTERVAL_MS,
} from "./config";
import type {
  ExportJobAdapter,
  ExportJobObservation,
  ExportJobSnapshot,
  ExportJobVerdict,
  PollingExportJob,
  TrackedExportJob,
} from "./types";

/** Whether My Exports lists this kind, so the copy may point there. */
export function isListedInMyExports(adapter: ExportJobAdapter): boolean {
  return adapter.listedInMyExports !== false;
}

/** `ready` counts only with a URL; `ready` without one keeps polling. */
export function isExportJobDone(snapshot: ExportJobSnapshot): boolean {
  return (
    snapshot.status === "failed" ||
    (snapshot.status === "ready" && !!snapshot.url)
  );
}

type Progress = Pick<
  PollingExportJob,
  "errorStreak" | "clientErrorStreak" | "lastOkAt" | "lastObservedAt"
>;

export interface ObservationJudgement {
  progress: Progress;
  verdict: ExportJobVerdict | null;
}

/**
 * Folds one poll result into a polling job. Returns null for a result the job
 * already saw (StrictMode re-runs, a shared cache entry). While the tab is
 * hidden the counters move but nothing settles: the refetch on focus decides.
 */
export function judgeObservation(
  job: PollingExportJob,
  observation: ExportJobObservation,
  visible: boolean,
): ObservationJudgement | null {
  if (observation.at <= job.lastObservedAt) return null;

  const progress: Progress =
    observation.kind === "status"
      ? {
          errorStreak: 0,
          clientErrorStreak: 0,
          lastOkAt: observation.at,
          lastObservedAt: observation.at,
        }
      : {
          errorStreak: job.errorStreak + 1,
          clientErrorStreak: observation.clientError ? job.clientErrorStreak + 1 : 0,
          lastOkAt: job.lastOkAt,
          lastObservedAt: observation.at,
        };
  if (!visible) return { progress, verdict: null };

  if (observation.kind === "status") {
    const { status, url, error, note } = observation.snapshot;
    if (status === "ready" && url) {
      return { progress, verdict: { outcome: "ready", url, note } };
    }
    if (status === "failed") {
      return { progress, verdict: { outcome: "failed", error } };
    }
  } else {
    // Only an answer no retry changes gives up early; a network error or a
    // 5xx keeps polling until the cap.
    const since = job.lastOkAt ?? job.pollingSince;
    if (
      progress.clientErrorStreak >= EXPORT_JOB_ERROR_STREAK &&
      observation.at - since >= EXPORT_JOB_LOST_AFTER_MS
    ) {
      return { progress, verdict: { outcome: "lost" } };
    }
  }

  return {
    progress,
    verdict: observation.at >= job.deadlineAt ? { outcome: "timed_out" } : null,
  };
}

/**
 * The deadline timer. A start request that never answered times out wherever
 * the tab is; a polling job only in a visible tab, else the refetch on focus
 * decides.
 */
export function judgeDeadline(
  job: TrackedExportJob,
  now: number,
  visible: boolean,
): ExportJobVerdict | null {
  if (now < job.deadlineAt) return null;
  return job.phase === "starting" || visible ? { outcome: "timed_out" } : null;
}

export function exportJobSlowAt(job: PollingExportJob): number {
  return job.pollingSince + EXPORT_JOB_BUDGET_MS;
}

/**
 * Whether a polling job turns slow now: once, and only in a visible tab (a
 * hidden one waits for the refetch on focus, which may find the file ready).
 */
export function judgeSlow(
  job: PollingExportJob,
  now: number,
  visible: boolean,
): boolean {
  return !job.slow && visible && now >= exportJobSlowAt(job);
}

export function exportJobPollInterval(job: PollingExportJob): number {
  return job.slow || job.errorStreak >= EXPORT_JOB_ERROR_STREAK
    ? EXPORT_JOB_SLOW_POLL_INTERVAL_MS
    : EXPORT_JOB_POLL_INTERVAL_MS;
}
